import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DollarSign, TrendingUp, TrendingDown, Building2, Calendar, AlertCircle, Download, ChevronDown, Table2, FileSpreadsheet, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [propertyStats, setPropertyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("all-time");
  const [filteredSummary, setFilteredSummary] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (summary) {
      calculateFilteredSummary();
      fetchPropertyStatsForPeriod();
    }
  }, [selectedPeriod, summary]);

  const calculateFilteredSummary = () => {
    if (selectedPeriod === "all-time") {
      setFilteredSummary({
        total_income: summary.total_income,
        total_expense: summary.total_expense,
        net_income: summary.net_income,
        properties_count: summary.properties_count
      });
    } else {
      // Find the selected month's data
      const monthData = summary.monthly_data.find(m => m.month === selectedPeriod);
      if (monthData) {
        setFilteredSummary({
          total_income: monthData.income,
          total_expense: monthData.expense,
          net_income: monthData.net,
          properties_count: summary.properties_count
        });
      }
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, statsRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`),
        axios.get(`${API}/properties/stats/all?period=all-time`)
      ]);
      setSummary(summaryRes.data);
      setPropertyStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyStatsForPeriod = async () => {
    try {
      const period = selectedPeriod === "all-time" ? "all-time" : selectedPeriod;
      const response = await axios.get(`${API}/properties/stats/all?period=${period}`);
      setPropertyStats(response.data);
    } catch (error) {
      console.error("Error fetching property stats:", error);
    }
  };

  const getAvailableMonths = () => {
    if (!summary || !summary.monthly_data) return [];
    return summary.monthly_data.map(m => ({
      value: m.month,
      label: new Date(m.month + '-01').toLocaleString('en-US', { month: 'long', year: 'numeric' })
    }));
  };

  const getPeriodLabel = () => {
    if (selectedPeriod === "all-time") return "All Time";
    const month = getAvailableMonths().find(m => m.value === selectedPeriod);
    return month ? month.label : "All Time";
  };

  const handleExportAll = async (format) => {
    const formatConfig = {
      csv: { endpoint: 'csv', extension: 'csv', name: 'CSV' },
      excel: { endpoint: 'excel', extension: 'xlsx', name: 'Excel' },
      pdf: { endpoint: 'pdf', extension: 'pdf', name: 'PDF' }
    };
    
    const config = formatConfig[format];
    if (!config) return;
    
    try {
      const response = await axios.get(`${API}/export/${config.endpoint}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `all_transactions.${config.extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`All transactions exported as ${config.name}!`);
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Failed to export transactions");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="dashboard-loading">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="dashboard-error">
        <div className="text-lg text-muted-foreground">No data available</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-in" data-testid="dashboard">
      <div className="mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-primary" data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-base text-muted-foreground mt-2">Overview of all your properties</p>
          </div>
          
          {/* Period Selector and Export */}
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="export-all-button">
                  <Download className="w-4 h-4 mr-2" />
                  Export All
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => handleExportAll('csv')} data-testid="export-all-csv">
                  <Table2 className="w-4 h-4 mr-2 text-emerald-600" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportAll('excel')} data-testid="export-all-excel">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                  Export as Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportAll('pdf')} data-testid="export-all-pdf">
                  <FileText className="w-4 h-4 mr-2 text-red-600" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Badge variant="outline" className="px-3 py-1.5 text-sm">
              {selectedPeriod === "all-time" ? "Cumulative View" : "Monthly View"}
            </Badge>
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-[200px]" data-testid="period-selector">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-time" data-testid="period-all-time">
                  All Time (Cumulative)
                </SelectItem>
                {getAvailableMonths().map(month => (
                  <SelectItem key={month.value} value={month.value} data-testid={`period-${month.value}`}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Period Indicator */}
      <div className="mb-6">
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="font-medium text-primary">
                Showing data for: <span className="font-bold">{getPeriodLabel()}</span>
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards */}
      {filteredSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300" data-testid="card-total-income">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-income" />
                Total Income
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="text-3xl font-bold text-income">{formatCurrency(filteredSummary.total_income)}</div>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300" data-testid="card-total-expense">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-expense" />
                Total Expenses
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="text-3xl font-bold text-expense">{formatCurrency(filteredSummary.total_expense)}</div>
            </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300" data-testid="card-net-income">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Net Income
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div
              className={`text-3xl font-bold ${
                filteredSummary.net_income >= 0 ? "text-income" : "text-expense"
              }`}
            >
              {formatCurrency(filteredSummary.net_income)}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300" data-testid="card-properties-count">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Properties
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="text-3xl font-bold text-primary">{filteredSummary.properties_count}</div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Monthly Chart */}
      <Card className="bg-white rounded-xl border border-border/60 shadow-sm" data-testid="monthly-chart-card">
        <CardHeader className="p-6 pb-2">
          <CardTitle className="text-2xl font-semibold text-primary">Monthly Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-4">
          {summary.monthly_data && summary.monthly_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={summary.monthly_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="month" stroke="#78716C" />
                <YAxis stroke="#78716C" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E7E5E4",
                    borderRadius: "0.75rem",
                  }}
                />
                <Legend />
                <Bar dataKey="income" fill="#10B981" name="Income" radius={[8, 8, 0, 0]} />
                <Bar dataKey="expense" fill="#F43F5E" name="Expense" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground" data-testid="no-monthly-data">
              No monthly data available. Add transactions to see the chart.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Month-wise Summary Section */}
      {summary && summary.monthly_data && summary.monthly_data.length > 0 && (
        <div className="mt-8">
          <div className="mb-6">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-primary">Month-wise Overview</h2>
            <p className="text-base text-muted-foreground mt-2">Total income, expenses, and net income across all properties by month</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {summary.monthly_data.slice().reverse().map((monthData) => {
              const monthName = new Date(monthData.month + '-01').toLocaleString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              });
              
              return (
                <Card
                  key={monthData.month}
                  className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300"
                  data-testid={`month-summary-${monthData.month}`}
                >
                  <CardHeader className="p-6 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-primary">{monthName}</CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">All Properties</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 pt-0 space-y-3">
                    {/* Income */}
                    <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-900">Total Income</span>
                      </div>
                      <span className="text-lg font-bold text-emerald-700">
                        {formatCurrency(monthData.income)}
                      </span>
                    </div>

                    {/* Expense */}
                    <div className="flex items-center justify-between p-3 bg-rose-50 rounded-lg border border-rose-100">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-rose-600" />
                        <span className="text-sm font-medium text-rose-900">Total Expenses</span>
                      </div>
                      <span className="text-lg font-bold text-rose-700">
                        {formatCurrency(monthData.expense)}
                      </span>
                    </div>

                    {/* Net Income */}
                    <div className={`flex items-center justify-between p-3 rounded-lg border ${
                      monthData.net >= 0 
                        ? 'bg-primary/5 border-primary/20' 
                        : 'bg-rose-50 border-rose-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">Net Income</span>
                      </div>
                      <span className={`text-xl font-bold ${
                        monthData.net >= 0 ? 'text-income' : 'text-expense'
                      }`}>
                        {formatCurrency(monthData.net)}
                      </span>
                    </div>

                    {/* Profitability Badge */}
                    <div className="pt-2">
                      <Badge 
                        className={`w-full justify-center py-1.5 ${
                          monthData.net >= 0
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-rose-100 text-rose-700 border-rose-200'
                        }`}
                      >
                        {monthData.net >= 0 ? '✓ Profitable Month' : '⚠ Loss Month'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-Property Stats & Reminders */}
      {propertyStats.length > 0 && (
        <div className="mt-8">
          <div className="mb-6">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-primary">Property Performance</h2>
            <p className="text-base text-muted-foreground mt-2">Daily and monthly net income for each property</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {propertyStats.map((stat) => (
              <Card
                key={stat.property_id}
                className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300"
                data-testid={`property-stat-${stat.property_id}`}
              >
                <CardHeader className="p-6 pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-semibold text-primary mb-1">
                        {stat.property_name}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">{stat.property_address}</p>
                    </div>
                    {stat.days_since_last_transaction !== null && stat.days_since_last_transaction > 1 && (
                      <Badge
                        variant="outline"
                        className={`ml-2 ${
                          stat.days_since_last_transaction > 3
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                        }`}
                        data-testid={`reminder-badge-${stat.property_id}`}
                      >
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {stat.days_since_last_transaction}d ago
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6 pt-0 space-y-4">
                  {/* Today's Stats */}
                  <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">Today</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-primary">
                        {formatCurrency(stat.today_net)}
                      </span>
                      <span className="text-xs text-muted-foreground">net income</span>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs">
                      <span className="text-income">↑ {formatCurrency(stat.today_income)}</span>
                      <span className="text-expense">↓ {formatCurrency(stat.today_expense)}</span>
                    </div>
                  </div>

                  {/* Period Stats (Monthly or Cumulative) */}
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium text-primary">
                        {selectedPeriod === "all-time" ? "All Time" : getPeriodLabel()}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-primary">
                        {formatCurrency(stat.monthly_net)}
                      </span>
                      <span className="text-xs text-muted-foreground">net income</span>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs">
                      <span className="text-income">↑ {formatCurrency(stat.monthly_income)}</span>
                      <span className="text-expense">↓ {formatCurrency(stat.monthly_expense)}</span>
                    </div>
                  </div>

                  {/* Reminder */}
                  {stat.days_since_last_transaction !== null && stat.days_since_last_transaction > 1 && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs">
                        <p className="font-medium text-amber-900">Transaction Reminder</p>
                        <p className="text-amber-700 mt-0.5">
                          Last entry was {stat.days_since_last_transaction} day{stat.days_since_last_transaction > 1 ? 's' : ''} ago
                          {stat.last_transaction_date && ` (${stat.last_transaction_date})`}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {stat.days_since_last_transaction === 0 && (
                    <div className="flex items-center gap-2 p-2 text-xs text-emerald-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                      Transaction added today
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}